import React, { useState, useMemo } from "react";
import clsx from "classnames";
import { useQuery, gql, useMutation } from "@apollo/client";
import { useStyles } from "./SetProfile.style";

import Button from "../../components/core/Button/Button";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyVariant } from "../../constants/typography";
import { Image } from "@onmo/ui";
import { TextField } from "../../components/core/TextField";
import { useTranslation } from "react-i18next";
import { Regex } from "../../models/regex";
import { useFetchFreeAvatars } from "../../hooks/resolvers/shop/useShopQuery";
import CheckIcon from "../../assets/icon/check-icon/check-icon_1x.webp";
import CheckIcon2x from "../../assets/icon/check-icon/check-icon_2x.webp";
import CheckIcon3x from "../../assets/icon/check-icon/check-icon_3x.webp";
import {
  applyInventoryGQL,
  meGQL,
  updateUserGQL,
  updateUserMetadataGQL,
} from "@onmo/graphql/graphql";
import { usePurchaseInventory } from "../../hooks/resolvers/shop/useShopMutation";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../../constants/historyGaming";

const SetProfile: React.FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { data: dataMe, refetch } = useQuery(meGQL);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [username, setUsername] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState<any | null>(null);

  const isInvalidUsername = !Regex.isValidUsername(username || "") || !username;

  const { freeAvatars: avatars, loading } = useFetchFreeAvatars();

  const [updateUser] = useMutation(updateUserGQL);
  const [updateUserMetadata] = useMutation(updateUserMetadataGQL);
  const [applyInventory] = useMutation(applyInventoryGQL);
  const purchaseInventory = usePurchaseInventory({ showPopup: false });

  React.useEffect(() => {
    if (!loading && avatars.length > 0 && !selectedAvatar) {
      setSelectedAvatar(avatars[0]);
    }
  }, [loading, avatars]);

  React.useEffect(() => {
    if (dataMe?.me?.username) {
      setUsername(dataMe.me.username);
    }
  }, [dataMe?.me?.username]);

  const handleNext = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);

      if (!selectedAvatar) return;
      await purchaseInventory(selectedAvatar);

      const payload = {
        username: username.trim(),
        avatarId: selectedAvatar.id,
      };

      let currentOnboarding = {};
      try {
        currentOnboarding = JSON.parse(dataMe?.me.metadata?.onboarding || "{}");
      } catch (err) {
        console.warn("Failed to parse onboarding metadata:", err);
        currentOnboarding = {};
      }

      const updatedOnboarding = {
        ...currentOnboarding,
        hasCheckedUserNameAvatar: true,
      };

      await Promise.all([
        updateUser({
          variables: {
            input: {
              username: payload.username,
            },
          },
        }),

        updateUserMetadata({
          variables: {
            input: {
              onboarding: JSON.stringify(updatedOnboarding),
            },
          },
        }),

        applyInventory({
          variables: {
            input: { inventoryId: payload.avatarId },
          },
        }),
      ]);

      await refetch();

      navigate(PageRoutes.HomePage);

      console.log("Updated username + avatar!");
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <img
          src="/pwa/vodacom/voda.svg"
          alt="Vodacom"
          className={classes.logo}
        />
      </div>

      <div className={classes.frame}>
        {/* Title */}
        <div className={classes.title}>
          <Typography variant={TypographyVariant.HeadingH4Bold}>
            {t("welcome_title")}
          </Typography>
        </div>

        {/* Username Input */}
        <div className={classes.fieldWrapper}>
          <div className={classes.subtitle}>
            <Typography variant={TypographyVariant.BodyB4Medium}>
              {t("enter_username_title")}
            </Typography>
          </div>

          <div>
            <TextField
              className={classes.inputTxt}
              fullWidth
              inputProps={{
                placeholder: t("common_Username"),
                value: username,
              }}
              descriptionMessage={
                isInvalidUsername
                  ? t(
                      "profile_Username can contain between 3 to 16 characters.",
                    )
                  : undefined
              }
              isShowClear
              onChangeValue={setUsername}
              isError={isInvalidUsername}
            />
          </div>
        </div>

        {/* Avatar Grid */}
        <div className={classes.avatarGrid}>
          {loading &&
            Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className={classes.avatarItem} />
            ))}

          {!loading &&
            avatars.map((avatar: any) => {
              const isActive = selectedAvatar === avatar;

              return (
                <div
                  key={avatar.id}
                  className={clsx(classes.avatarItem, {
                    [classes.activeAvatar]: isActive,
                  })}
                  onClick={() => setSelectedAvatar(avatar)}
                >
                  {/* Avatar Image */}
                  <img
                    src={avatar?.data?.url}
                    alt={
                      avatar?.metadata?.translations?.englishTitle ||
                      avatar?.title
                    }
                  />

                  {/* Check Icon on Active */}
                  {isActive && (
                    <div className={classes.checkIcon}>
                      <Image
                        src={CheckIcon}
                        src2x={CheckIcon2x}
                        src3x={CheckIcon3x}
                        width={24}
                        height={24}
                      />
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Button */}
        <div className={classes.buttonWrapper}>
          <Button
            variant="contained"
            color="red"
            size="large"
            fullWidth
            loading={isSubmitting}
            disabled={
              !username || !selectedAvatar || isInvalidUsername || isSubmitting
            }
            onClick={handleNext}
          >
            {t("next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SetProfile;
